import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MailCheck } from 'lucide-react';
import { notify } from '../../components/Notify';
import AuthShell from '../../components/AuthShell';
import { TextField, PasswordField } from '../../components/Fields';
import { recuperarContaStatusApi, recuperarContaIniciarApi } from '../../api';
import { formatCPF, onlyDigits, validateCPF, validateEmail, validateDateOfBirth } from '../../validators';
import { useCooldown, formatarCooldown } from '../../hooks/useCooldown';
import { abrirWhatsappSuporte } from '../../config';

export default function RecuperarConta() {
    const [cpf, setCpf] = useState('');
    const [dataNascimento, setDataNascimento] = useState('');
    const [email, setEmail] = useState('');
    const [senha, setSenha] = useState('');
    const [erros, setErros] = useState({});
    const [enviando, setEnviando] = useState(false);
    const [enviado, setEnviado] = useState(false);
    const [destino, setDestino] = useState('');
    const [bloqueio, setBloqueio] = useState(null); // { seconds_left, tier }
    const cooldown = useCooldown('ueb_recuperar_conta_cooldown', 10);

    // Ao completar o CPF, consulta se está bloqueado por tentativas.
    const checarStatus = async (valor) => {
        const d = onlyDigits(valor);
        if (d.length !== 11) return;
        try {
            const res = await recuperarContaStatusApi({ cpf: d });
            if (res?.data?.blocked) setBloqueio({ seconds_left: res.data.seconds_left, tier: res.data.tier });
            else setBloqueio(null);
        } catch { /* silencioso */ }
    };

    const validar = () => {
        const e = {};
        const vc = validateCPF(cpf); if (!vc.valid) e.cpf = vc.error;
        const vd = validateDateOfBirth(dataNascimento); if (!vd.valid) e.dataNascimento = vd.error;
        const ve = validateEmail(email); if (!ve.valid) e.email = ve.error;
        if (!senha || senha.length < 6) e.senha = 'Mínimo de 6 caracteres.';
        setErros(e);
        return Object.keys(e).length === 0;
    };

    const enviar = async (ev) => {
        ev.preventDefault();
        if (enviando || cooldown.isActive || !validar()) return;
        setEnviando(true);
        try {
            const res = await recuperarContaIniciarApi({ cpf, dataNascimento, email, senha });
            setDestino(res?.data?.email_destino || email);
            setEnviado(true);
            cooldown.start();
        } catch (err) {
            const status = err?.response?.status;
            const data = err?.response?.data || {};
            if (status === 429) setBloqueio({ seconds_left: data.seconds_left, tier: data.tier });
            else if (status === 409 && data.error === 'email_em_uso') notify('Este email já está em uso por outra conta.');
            else if (status === 401) notify('Dados não conferem. Verifique CPF e data de nascimento.');
            else notify(data.error || data.message || 'Não foi possível recuperar a conta.');
        } finally {
            setEnviando(false);
        }
    };

    if (enviado) {
        return (
            <AuthShell>
                <div className="ueb-auth-card pcenter">
                    <div className="ueb-auth-ico"><MailCheck size={40} /></div>
                    <h1>Verifique seu email</h1>
                    <p className="ueb-auth-sub">
                        Enviamos um link de ativação para <strong>{destino}</strong>. Abra o email e
                        confirme para ativar seu acesso.
                    </p>
                    <Link className="pbtn pbtn--ghost pbtn--block" to="/login">Voltar ao login</Link>
                </div>
            </AuthShell>
        );
    }

    return (
        <AuthShell>
            <form className="ueb-auth-card" onSubmit={enviar}>
                <h1>Recuperar conta</h1>
                <p className="ueb-auth-sub">
                    Para alunos antigos, sem email/senha. Confirme seus dados e escolha um email e
                    senha para acessar o portal.
                </p>

                {bloqueio && (
                    <div className="palert palert--danger" style={{ marginBottom: 16 }}>
                        <span>
                            Muitas tentativas. Tente novamente em{' '}
                            <strong>{formatarCooldown(bloqueio.seconds_left || 0)}</strong>.
                        </span>
                    </div>
                )}

                <TextField
                    id="cpf"
                    label="CPF"
                    inputMode="numeric"
                    placeholder="000.000.000-00"
                    value={cpf}
                    onChange={(e) => setCpf(formatCPF(e.target.value))}
                    onBlur={(e) => checarStatus(e.target.value)}
                    error={erros.cpf}
                />
                <TextField
                    id="dataNascimento"
                    label="Data de nascimento"
                    type="date"
                    value={dataNascimento}
                    onChange={(e) => setDataNascimento(e.target.value)}
                    error={erros.dataNascimento}
                />
                <TextField
                    id="email"
                    label="Novo email"
                    type="email"
                    placeholder="voce@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    error={erros.email}
                />
                <PasswordField
                    id="senha"
                    label="Nova senha"
                    autoComplete="new-password"
                    placeholder="Mínimo de 6 caracteres"
                    value={senha}
                    onChange={(e) => setSenha(e.target.value)}
                    error={erros.senha}
                />

                <button className="pbtn pbtn--primary pbtn--block pbtn--lg" type="submit" disabled={enviando || cooldown.isActive || !!bloqueio}>
                    {enviando ? <span className="pspin" /> : cooldown.isActive ? `Aguarde ${formatarCooldown(cooldown.secondsLeft)}` : 'Ativar minha conta'}
                </button>

                <p className="ueb-auth-foot">
                    Precisa de ajuda? <button type="button" className="plink" onClick={abrirWhatsappSuporte}>Falar no WhatsApp</button>
                </p>
                <p className="ueb-auth-foot"><Link className="plink" to="/login">Voltar ao login</Link></p>
            </form>
        </AuthShell>
    );
}
